// =============================================================================
// src/features/analytics/analytics-mention-markup.ts - Feed e detalhe de mencoes.
// =============================================================================

import { escapeHtml } from '../../app/dom';
import { analyticsSourceLabelFromUrl } from '../../data/analytics-overlay-format';
import {
  buildAnalyticsFeedPaginationMarkup,
  type AnalyticsFeedPageView,
} from './analytics-feed-pagination-markup';
import { buildAnalyticsFeedSortMarkup } from './analytics-feed-sort-markup';
import {
  analyticsFeedPageAvailable,
  getAnalyticsFeedPageState,
} from './analytics-feed-page-store';
import { ANALYTICS_FEED_PAGE_SIZE } from '../../data/analytics-feed-page-query';
import { getAnalyticsViewState } from './analytics-view-state';
import { type OmarAnalyticsMention } from './analytics-data';
import { ANALYTICS_IMPACT_LOW_CONFIDENCE, OMAR_ANALYTICS_IMPACT_LABELS } from './analytics-labels';
import { analyticsDemoTagMarkup } from './analytics-demo-tag';
import {
  analyticsLiveSource,
  getAnalyticsActorTotals,
  getAnalyticsFeedItems,
  getAnalyticsLedeTotals,
  getAnalyticsThemeWindow,
} from './analytics-live-store';
import { buildAnalyticsChartPanelMarkup } from './analytics-summary-markup';

/**
 * Mencoes da LISTA: a pagina servida quando a paginacao existe (live), senao a
 * base demonstrativa. Fonte unica — o feed, o pre-render de detalhes e o
 * contador leem daqui, para nunca divergirem entre si.
 */
export function analyticsListedMentions(): readonly OmarAnalyticsMention[] {
  return analyticsFeedPageAvailable() ? getAnalyticsFeedPageState().mentions : getAnalyticsFeedItems();
}

/** Estado da nav de paginacao a partir da pagina viva. */
export function analyticsFeedPageView(): AnalyticsFeedPageView {
  const pageState = getAnalyticsFeedPageState();
  return {
    page: pageState.page,
    pageSize: ANALYTICS_FEED_PAGE_SIZE,
    totalItems: pageState.totalItems,
    hasNextPage: pageState.hasNextPage,
    isLoading: pageState.isLoading,
  };
}

/** A nav so existe onde ha conjunto para percorrer — no mock nao ha. */
function analyticsFeedPaginationSlotMarkup(): string {
  if (!analyticsFeedPageAvailable()) return '';
  return buildAnalyticsFeedPaginationMarkup(analyticsFeedPageView());
}

/** Renderiza o modulo completo de mencoes monitoradas. */
export function buildAnalyticsMentionsViewMarkup(): string {
  return `<section class="u-analytics-module is-active" data-analytics-module-view="mentions" aria-labelledby="analyticsFeedTitle">
    <div class="u-analytics-mentions-lede">${analyticsMentionsDigestCardMarkup()}${buildAnalyticsChartPanelMarkup('analyticsMentionsChart', 'Mencoes e alcance por dia')}</div>
    ${analyticsActiveFiltersMarkup()}
    <div class="u-analytics-panel u-analytics-feed-panel">
      ${analyticsMentionsHeaderMarkup()}
      <div class="u-analytics-feed">${buildAnalyticsFeedCardsMarkup(analyticsListedMentions())}</div>
      ${analyticsFeedPaginationSlotMarkup()}
    </div>
  </section>`;
}

/** Pre-renderiza os detalhes clicaveis das mencoes. */
export function buildAnalyticsDetailShellMarkup(): string {
  return `<section class="u-analytics-detail" id="analyticsMentionDetail" aria-live="polite" hidden>
    ${buildAnalyticsDetailCardsMarkup(analyticsDetailMentions())}
  </section>`;
}

/** Uniao (sem repetir) da pagina listada com os destaques: clicar num card de
 *  QUALQUER uma das duas telas precisa achar o detalhe pre-renderizado. */
function analyticsDetailMentions(): readonly OmarAnalyticsMention[] {
  const porId = new Map<string, OmarAnalyticsMention>();
  for (const mention of [...analyticsListedMentions(), ...getAnalyticsFeedItems()]) {
    porId.set(mention.mentionId, mention);
  }
  return [...porId.values()];
}

/**
 * Modulo Destaques: a selecao curada de hoje (recentes + top engajamento + top
 * comentarios + top views + top por canal), preservada quando a lista de Mencoes
 * virou janela paginada sobre o conjunto. Decisao do usuario nos comps: cada tela
 * responde uma pergunta — "o que importou" aqui, "tudo, na ordem que eu escolher"
 * na outra — em vez de os mesmos itens aparecerem duas vezes na mesma tela.
 */
export function buildAnalyticsDestaquesViewMarkup(): string {
  const destaques = getAnalyticsFeedItems();
  return `<section class="u-analytics-module" data-analytics-module-view="destaques" aria-labelledby="analyticsDestaquesTitle" hidden>
    <div class="u-analytics-panel u-analytics-feed-panel">
      <div class="u-analytics-panel-head">
        <div><span>Selecao do recorte ativo</span><h2 id="analyticsDestaquesTitle">Destaques</h2></div>
        <b>${destaques.length}</b>
      </div>
      <p class="u-analytics-destaques-note">O que mais circulou: mais recentes, maior engajamento, mais comentados, mais vistos e o topo de cada canal.</p>
      <div class="u-analytics-destaques-feed">${buildAnalyticsFeedCardsMarkup(destaques)}</div>
    </div>
  </section>`;
}

/** Cards do feed para um conjunto explícito (re-render do drill-down por ator). */
export function buildAnalyticsFeedCardsMarkup(mentions: readonly OmarAnalyticsMention[]): string {
  return mentions.map(analyticsMentionMarkup).join('');
}

/** Detalhes pré-renderizados para um conjunto explícito (par do re-render). */
export function buildAnalyticsDetailCardsMarkup(mentions: readonly OmarAnalyticsMention[]): string {
  return mentions.map(analyticsMentionDetailMarkup).join('');
}

/** Renderiza duas mencoes prioritarias para o painel geral. */
export function buildAnalyticsMentionPreviewMarkup(): string {
  const priorityMentions = getAnalyticsFeedItems().slice(0, 2);
  return `<div class="u-analytics-preview-feed">${priorityMentions.map(analyticsMentionPreviewCardMarkup).join('')}</div>`;
}

function analyticsMentionsHeaderMarkup(): string {
  return `<div class="u-analytics-panel-head"><div><span id="analyticsActiveFilterLabel">Todos os canais</span><h2 id="analyticsFeedTitle">Mencoes recentes</h2></div><b id="analyticsFeedCount">${getAnalyticsFeedItems().length}</b></div>
    ${buildAnalyticsFeedSortMarkup(getAnalyticsViewState().sort, analyticsLiveSource())}`;
}

function analyticsMentionsDigestCardMarkup(): string {
  // Live (2026-07-25, "remover os conteudos mockados"): o card vira resumo REAL
  // do periodo — titulo do tema lider, numeros vivos, sem tag demonstracao e sem
  // CTA (a aba Resumo de IA foi desativada ate existir IA de verdade, F3).
  if (analyticsLiveSource() === 'live') {
    const leadTheme = getAnalyticsThemeWindow()[0];
    const liveTitle = leadTheme
      ? `${leadTheme.sliceLabel} lidera o volume do periodo`
      : 'Monitoramento ao vivo do periodo';
    return `<aside class="u-analytics-digest-card"><span>Resumo do periodo</span><h2>${escapeHtml(liveTitle)}</h2>
    <p>${analyticsDigestCardLedeMarkup()}</p></aside>`;
  }
  return `<aside class="u-analytics-digest-card"><span>Resumo de IA${analyticsDemoTagMarkup()}</span><h2>Tensao na BR-319 agita cenario politico de Omar Aziz</h2>
    <p>${analyticsDigestCardLedeMarkup()}</p>
    <button type="button" data-analytics-module-shortcut="digest">Ver resumo completo</button></aside>`;
}

/** Numeros do card vem do getter para nao contradizer os KPIs quando ao vivo
 * (a prosa mock cita alcance/engajamento que nao existem na coleta viva). */
function analyticsDigestCardLedeMarkup(): string {
  const ledeTotals = getAnalyticsLedeTotals();
  if (analyticsLiveSource() === 'live') {
    return `${escapeHtml(ledeTotals.mentionsLabel)} mencoes no recorte ${escapeHtml(ledeTotals.periodLabel)}, direto do monitoramento ao vivo.`;
  }
  return '2.022 mencoes, 87,4 mil de alcance e 658 mil interacoes. TikTok concentrou 76% do engajamento — prioridade: curto-formato e resposta em 2h.';
}

/** Máximo de chips de personagem (top por presença nos cards carregados). */
const ANALYTICS_ACTOR_CHIPS_MAX = 7;

/**
 * Faixa acima do feed: no LIVE vira o filtro de PERSONAGENS (atores políticos
 * casados nas menções carregadas, com contagem por card — decisão 2026-07-25,
 * substitui os chips estáticos BR-319/"Manaus critica" que não filtravam nada).
 * No fallback mock a faixa original permanece intacta.
 */
function analyticsActiveFiltersMarkup(): string {
  // Contagens REAIS por personagem (v_actor_platform_window_30d, janela 30d) —
  // NÃO a amostra do feed: clicar no chip busca o conteúdo do ator no servidor
  // (drill-down; fix 2026-07-25 do "Omar×Instagram = 0" na amostra local).
  const actorTotals = getAnalyticsActorTotals().slice(0, ANALYTICS_ACTOR_CHIPS_MAX);
  if (actorTotals.length === 0) {
    return `<div class="u-analytics-active-filters" aria-label="Filtros ativos">
    <span>Todos os canais</span><span>BR-319</span><span>Manaus critica</span>
    <button type="button" data-analytics-local-action="Filtros salvos">Salvar filtros</button>
    <button type="button" data-analytics-local-action="Filtros limpos">Limpar filtros</button>
  </div>`;
  }
  const actorButtons = actorTotals
    .map((actorTotal) => `<button type="button" data-analytics-actor-filter="${escapeHtml(actorTotal.actorSlug)}">${escapeHtml(actorTotal.actorName)}<b>${actorTotal.totalMentions}</b></button>`)
    .join('');
  return `<div class="u-analytics-active-filters" aria-label="Personagens monitorados">
    <span>Personagens</span>
    <button type="button" class="is-active" data-analytics-actor-filter="Todos">Todos</button>
    ${actorButtons}
    <button type="button" data-analytics-clear-filters>Limpar filtros</button>
  </div>`;
}

function analyticsMentionMarkup(mention: OmarAnalyticsMention): string {
  const sentimentAttr = mention.sentiment ? ` data-analytics-sentiment="${mention.sentiment}"` : '';
  const actorSlugs = mention.actorSlugs ?? [];
  const actorsAttr = actorSlugs.length > 0 ? ` data-analytics-actors="${escapeHtml(actorSlugs.join('|'))}"` : '';
  const relations = mention.relations ?? [];
  const relationsAttr = relations.length > 0 ? ` data-analytics-relations="${escapeHtml(relations.join('|'))}"` : '';
  const authorAttr = mention.rawAuthorHandle
    ? ` data-analytics-author-handle="${escapeHtml(mention.rawAuthorHandle)}"`
    : '';
  // Escopo Amazonas (017/018) — ausente no mock, por isso omitido quando nulo,
  // mesmo padrão null-safe do badge de sentimento.
  const relevanceAttr = mention.relevanceVerdict
    ? ` data-analytics-relevance="${escapeHtml(mention.relevanceVerdict)}"`
    : '';
  // 025: relacao com o VIP + substancia do comentario (so itens live).
  const vipAttr = mention.vipRelation ? ` data-analytics-vip="${escapeHtml(mention.vipRelation)}"` : '';
  const substanceAttr = mention.textSubstance ? ` data-analytics-substance="${escapeHtml(mention.textSubstance)}"` : '';
  return `<article class="u-analytics-mention" data-analytics-feed data-analytics-platform="${escapeHtml(mention.platform)}"${sentimentAttr}${actorsAttr}${relationsAttr}${authorAttr}${relevanceAttr}${vipAttr}${substanceAttr} data-analytics-recent-rank="${mention.recentRank}" data-analytics-popular-rank="${mention.popularRank}">
    <button class="u-analytics-mention-open" type="button" data-analytics-open-mention="${mention.mentionId}">
      ${analyticsMentionSummaryMarkup(mention)}
    </button>
    ${analyticsMentionMetricsMarkup(mention)}${analyticsMentionTagsMarkup(mention)}${analyticsMentionActionsMarkup(mention)}
  </article>`;
}

function analyticsMentionSummaryMarkup(mention: OmarAnalyticsMention): string {
  return `<header><span class="u-analytics-source-avatar">${analyticsAvatarInitials(mention.author)}</span><div><strong>${escapeHtml(mention.title)}</strong><small><i class="ti ${analyticsPlatformIcon(mention.platform)}"></i>${escapeHtml(mention.author)} - ${escapeHtml(mention.authorHandle)} - ${escapeHtml(mention.time)} - ${escapeHtml(mention.authorReach)}</small></div>${analyticsMentionBadgesMarkup(mention)}</header>
    <p>${escapeHtml(mention.text)}</p><footer><span>${escapeHtml(mention.mediaKind)} - ${escapeHtml(mention.platform)}</span>${analyticsMentionFooterSignalsMarkup(mention)}</footer>`;
}

/** Terceira coluna do header: impacto (em cima) e escopo; vazio some. */
function analyticsMentionBadgesMarkup(mention: OmarAnalyticsMention): string {
  const badges = `${analyticsImpactTagMarkup(mention)}${analyticsScopeBadgeMarkup(mention)}`;
  return badges ? `<div class="u-analytics-mention-badges">${badges}</div>` : '';
}

/**
 * Tag de impacto para o Omar (036, 2026-09-23): ponto + rótulo + confiança do Jev.
 * Discreta de propósito — a cor mora só no ponto (verde/vermelho são vocabulário
 * de dado, não accent). Confiança < 60% esmaece em vez de sumir (decisão do
 * usuário). Some quando não há veredito: item anterior à 036 fica sem selo.
 *
 * @example analyticsImpactTagMarkup({ ...mention, sentiment: 'positive', sentimentConfidence: 0.96 })
 * // '<span class="u-analytics-impact-tag" ...>...Promotor<b>96%</b></span>'
 */
export function analyticsImpactTagMarkup(
  mention: Pick<OmarAnalyticsMention, 'sentiment' | 'sentimentConfidence'>,
): string {
  if (!mention.sentiment) return '';
  const label = OMAR_ANALYTICS_IMPACT_LABELS[mention.sentiment];
  const confidence = mention.sentimentConfidence ?? null;
  const percent = confidence === null ? null : `${Math.round(confidence * 100)}%`;
  const lowClass = confidence !== null && confidence < ANALYTICS_IMPACT_LOW_CONFIDENCE ? ' is-incerto' : '';
  const aria = `Impacto para o Omar: ${label.toLowerCase()}${percent ? ` — confiança do Jev ${percent}` : ''}`;
  return `<span class="u-analytics-impact-tag is-${label.toLowerCase()}${lowClass}" title="${aria}" aria-label="${aria}"><i class="u-analytics-impact-dot" aria-hidden="true"></i>${label}${percent ? `<b>${percent}</b>` : ''}</span>`;
}

/** Rotulo da relacao com o VIP (025) — badge "Omar" azul no card. */
const ANALYTICS_VIP_RELATION_LABELS: Readonly<Record<string, string>> = {
  publicacao_propria: 'Publicacao do Omar',
  citacao: 'Cita o Omar',
  comentario_em_post_proprio: 'Comentario em post do Omar',
};

/** Badge de escopo (2026-09-02): "Omar" quando ha relacao com o VIP, senao
 * Nucleo/Contexto — antes o veredito so virava data-attribute e o operador nao
 * conseguia distinguir um item de contexto de um do cenario politico. */
function analyticsScopeBadgeMarkup(mention: OmarAnalyticsMention): string {
  if (!mention.relevanceVerdict) return '';
  const vipLabel = mention.vipRelation ? ANALYTICS_VIP_RELATION_LABELS[mention.vipRelation] : undefined;
  if (vipLabel) return `<span class="u-analytics-scope-badge is-omar">${escapeHtml(vipLabel)}</span>`;
  const verdictLabel = mention.relevanceVerdict === 'contexto' ? 'Contexto' : 'Nucleo';
  return `<span class="u-analytics-scope-badge is-${escapeHtml(mention.relevanceVerdict)}">${verdictLabel}</span>`;
}

function analyticsMentionFooterSignalsMarkup(mention: OmarAnalyticsMention): string {
  const signalParts = [mention.emotion, mention.reach ? `${mention.reach} alcance` : null, mention.contextNote ?? null]
    .filter((signalPart): signalPart is string => Boolean(signalPart));
  if (signalParts.length === 0) return '';
  return `<span>${escapeHtml(signalParts.join(' - '))}</span>`;
}

function analyticsMentionMetricsMarkup(mention: OmarAnalyticsMention): string {
  const metricEntries: readonly (readonly [string, string] | null)[] = [
    mention.score ? ['Score', mention.score] : null,
    // Views só existem no item live com contagem > 0 (TikTok/Reels) — mock omite.
    mention.views ? ['Views', mention.views] : null,
    ['Interacoes', mention.interactions],
    ['Comentarios', mention.comments],
    ['Shares', mention.shares],
  ];
  // Notícia de portal (RSS) não tem engajamento: métrica zerada é ruído e some
  // (pedido 2026-07-25). Em rede social, zero É informação — continua visível.
  const visibleMetrics = metricEntries.filter(
    (metricEntry): metricEntry is readonly [string, string] =>
      metricEntry !== null && !(mention.mediaKind === 'noticia' && metricEntry[1] === '0'),
  );
  if (visibleMetrics.length === 0) return '';
  return `<div class="u-analytics-mention-metrics">
    ${visibleMetrics.map(([metricLabel, metricValue]) => analyticsMentionMetricMarkup(metricLabel, metricValue)).join('')}
  </div>`;
}

function analyticsMentionMetricMarkup(metricLabel: string, metricValue: string): string {
  return `<span><b>${escapeHtml(metricValue)}</b>${escapeHtml(metricLabel)}</span>`;
}

function analyticsMentionTagsMarkup(mention: OmarAnalyticsMention): string {
  return `<div class="u-analytics-tags">
    ${mention.tags.map((mentionTag) => `<span>${escapeHtml(mentionTag)}</span>`).join('')}
  </div>`;
}

function analyticsMentionActionsMarkup(mention: OmarAnalyticsMention): string {
  // "Marcar" e "Adicionar ao relatorio" DESABILITADOS (pedido do usuario
  // 2026-07-25): eram toasts cosmeticos que fingiam funcionar — ficam visiveis
  // porem inertes (padrao da doca de inteligencia) ate existir feature real.
  // Para religar: remover disabled/title e devolver data-analytics-local-action.
  return `<div class="u-analytics-mention-actions">
    ${analyticsVisitActionMarkup(mention)}
    <button type="button" disabled title="Disponivel na proxima fase"><i class="ti ti-tag"></i>Marcar</button>
    ${analyticsBlockAuthorActionMarkup(mention)}
    ${analyticsScopeOverrideActionMarkup(mention)}
    <button type="button" disabled title="Disponivel na proxima fase"><i class="ti ti-file-plus"></i>Adicionar ao relatorio</button>
  </div>`;
}

/** Item live tem URL real → "Visitar" e um hyperlink de verdade (nova aba);
 * item live SEM url (nem própria nem do post-raiz) omite a ação em vez de
 * fingir com toast. Mock segue o toast cosmético. */
function analyticsVisitActionMarkup(mention: OmarAnalyticsMention): string {
  if (mention.sourceUrlLabel.startsWith('http')) {
    return `<a href="${escapeHtml(mention.sourceUrlLabel)}" target="_blank" rel="noopener noreferrer"><i class="ti ti-eye"></i>Visitar</a>`;
  }
  if (mention.rawPlatform) return '';
  return '<button type="button" data-analytics-local-action="Visita registrada"><i class="ti ti-eye"></i>Visitar</button>';
}

/** Item live: bloqueio REAL na blocklist (curadoria de falso positivo — o
 * conteudo do autor some e nao volta nas proximas importacoes do Dagster,
 * pois as views de serving o excluem). Mock: toast cosmetico original. */
function analyticsBlockAuthorActionMarkup(mention: OmarAnalyticsMention): string {
  if (mention.rawAuthorHandle && mention.rawPlatform) {
    return `<button type="button" data-analytics-block-author="${escapeHtml(mention.rawAuthorHandle)}" data-analytics-block-platform="${escapeHtml(mention.rawPlatform)}"><i class="ti ti-ban"></i>Bloquear autor</button>`;
  }
  return '<button type="button" data-analytics-local-action="Autor bloqueado"><i class="ti ti-ban"></i>Bloquear autor</button>';
}

/** Item live: marca o CONTEUDO fora do escopo Amazonas (017/018). Complementa o
 * bloqueio de autor — portal local monitorado republicando pauta nacional nao pode
 * ser resolvido bloqueando o portal inteiro. Mock: toast cosmetico. */
function analyticsScopeOverrideActionMarkup(mention: OmarAnalyticsMention): string {
  if (mention.relevanceVerdict) {
    return `<button type="button" data-analytics-scope-override="${escapeHtml(mention.mentionId)}"><i class="ti ti-map-off"></i>Fora de escopo</button>`;
  }
  return '<button type="button" data-analytics-local-action="Marcado fora de escopo"><i class="ti ti-map-off"></i>Fora de escopo</button>';
}

function analyticsMentionPreviewCardMarkup(mention: OmarAnalyticsMention): string {
  return `<button class="u-analytics-preview-card" type="button" data-analytics-open-mention="${mention.mentionId}">
    <span>${escapeHtml(mention.platform)} - ${escapeHtml(mention.time)}</span>
    <strong>${escapeHtml(mention.title)}</strong>
    ${analyticsImpactTagMarkup(mention)}
  </button>`;
}

function analyticsMentionDetailMarkup(mention: OmarAnalyticsMention): string {
  return `<article class="u-analytics-detail-card" data-analytics-detail="${mention.mentionId}" hidden>
    <button type="button" class="u-analytics-back" data-analytics-back><i class="ti ti-arrow-left"></i> Voltar as mencoes</button>
    <div class="u-analytics-detail-layout"><main class="u-analytics-detail-story">${analyticsDetailStoryMarkup(mention)}</main>${analyticsDetailContextMarkup(mention)}</div>
  </article>`;
}

function analyticsDetailStoryMarkup(mention: OmarAnalyticsMention): string {
  // Secoes de narrativa de IA (risco/acao/emocao) somem no item live — chegam no F3.
  const gridSections = [
    mention.detailRisk ? analyticsDetailSectionMarkup('Risco narrativo', mention.detailRisk) : '',
    mention.detailAction ? analyticsDetailSectionMarkup('Acao recomendada', mention.detailAction) : '',
    mention.emotion && mention.sentiment
      ? analyticsDetailSectionMarkup('Emocao dominante', `${mention.emotion} - ${OMAR_ANALYTICS_IMPACT_LABELS[mention.sentiment]}`)
      : '',
  ].join('');
  const detailGrid = gridSections ? `<div class="u-analytics-detail-grid">${gridSections}</div>` : '';
  return `${analyticsDetailHeroMarkup(mention)}${analyticsDetailSourceMarkup(mention)}
    ${detailGrid}
    ${analyticsDetailListMarkup('Sinais observados', mention.detailSignals)}${analyticsDetailListMarkup('Proximas acoes', mention.recommendedActions)}`;
}

function analyticsDetailHeroMarkup(mention: OmarAnalyticsMention): string {
  const heroMeta = [mention.platform, mention.time, mention.emotion]
    .filter((heroPart): heroPart is string => Boolean(heroPart))
    .join(' - ');
  const heroLead = mention.detailLead ? `<p>${escapeHtml(mention.detailLead)}</p>` : '';
  return `<div class="u-analytics-detail-hero"><span>${escapeHtml(heroMeta)}</span><h2>${escapeHtml(mention.title)}</h2>${heroLead}</div>`;
}

function analyticsDetailSourceMarkup(mention: OmarAnalyticsMention): string {
  // O campo guarda a URL (e o href de "Visitar"); aqui vai o ROTULO legivel dela.
  return `<section class="u-analytics-detail-source"><div><strong>${escapeHtml(mention.author)}</strong><span>${escapeHtml(analyticsSourceLabelFromUrl(mention.sourceUrlLabel))}</span></div><p>${escapeHtml(mention.text)}</p></section>`;
}

function analyticsDetailSectionMarkup(sectionTitle: string, sectionBody: string): string {
  return `<section><span>${escapeHtml(sectionTitle)}</span><p>${escapeHtml(sectionBody)}</p></section>`;
}

function analyticsDetailListMarkup(listTitle: string, listEntries: readonly string[]): string {
  if (listEntries.length === 0) return '';
  return `<section class="u-analytics-detail-list"><h3>${escapeHtml(listTitle)}</h3>${listEntries.map((listEntry) => `<p><i class="ti ti-circle-check"></i>${escapeHtml(listEntry)}</p>`).join('')}</section>`;
}

function analyticsDetailContextMarkup(mention: OmarAnalyticsMention): string {
  const savedSummary = mention.detailSummary ? `<h3>${escapeHtml(mention.detailSummary)}</h3>` : '';
  // Mesmo tratamento das acoes do card (2026-07-25): botao cosmético vira inerte.
  return `<aside class="u-analytics-detail-context"><span>Resumo salvo</span>${savedSummary}${analyticsMentionMetricsMarkup(mention)}${analyticsMentionTagsMarkup(mention)}<button type="button" class="u-analytics-soft-action" disabled title="Disponivel na proxima fase">Adicionar ao relatorio</button></aside>`;
}

function analyticsAvatarInitials(authorName: string): string {
  const authorParts = authorName.split(/\s+/).filter(Boolean).slice(0, 2);
  return authorParts.map((authorPart) => authorPart[0]?.toUpperCase() ?? '').join('');
}

function analyticsPlatformIcon(platform: string): string {
  if (platform === 'TikTok') return 'ti-brand-tiktok';
  if (platform === 'Facebook') return 'ti-brand-facebook';
  if (platform === 'X/Twitter') return 'ti-brand-x';
  if (platform === 'Noticias') return 'ti-news';
  if (platform === 'Blogs') return 'ti-message-2';
  return 'ti-world';
}
